import { useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';

import {
  Box,
  Button,
  IconButton,
  Typography,
  Menu,
  MenuItem,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import moment from 'moment';

import { FlagsApi } from '@/api/api.flags';
import { IcDotSpread } from '@/assets/images';
import { DataGrid, FlagChips } from '@/components';
import { DataGridColSettings } from '@/components/DataGrid/utils';
import { LocalStoragePrefix } from '@/constants';
import { useWantedPersonFlagSync } from '@/hooks';
import { FlagOrigin, IPersonFlag, SystemFlagType } from '@/models';
import { MasterIndexDetailsContext } from '@/pages/MasterIndex/components/MasterIndexDetailsPage';
import { useUserStore } from '@/store';
import {
  canUserAssignFlag,
  canUserDeleteFlag,
  canUserEditFlag,
  canUserViewFlag,
  formatPersonName,
  getLocalStorageItem,
  setLocalStorageItem,
} from '@/utils';

import { FlagSidePanel } from './components/FlagSidePanel';
import { PersonFlagDeleteModal } from './components/PersonFlagDeleteModal';
import MasterIndexPersonBase from './MasterIndexPersonBase';

const personFlagsColumnSettings: DataGridColSettings[] = [
  {
    field: 'flagName',
    headerName: 'Flag Name',
    width: 200,
    visible: true,
  },
  {
    field: 'code',
    headerName: 'Code',
    width: 100,
    visible: true,
  },
  {
    field: 'addedBy',
    headerName: 'Added By',
    width: 200,
    visible: true,
  },
  {
    field: 'dateAssigned',
    headerName: 'Date',
    width: 150,
    visible: true,
  },
  {
    field: 'reason',
    headerName: 'Reason',
    width: 200,
    visible: true,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    width: 80,
    visible: true,
  },
];

const MasterIndexPersonFlags = ({
  viewId,
  onFlagsUpdate: onFlagsUpdateProp,
  readOnly = false,
}: {
  viewId?: string;
  onFlagsUpdate?: () => void;
  readOnly?: boolean;
}) => {
  const { id } = useParams<{ id: string }>();
  const personId = id || viewId;
  const { user } = useUserStore();
  const outletContext = useOutletContext<MasterIndexDetailsContext>();
  const { onFlagsUpdate: onFlagsUpdateContext } = outletContext || {};
  const onFlagsUpdate = onFlagsUpdateProp || onFlagsUpdateContext;
  // Check if user has permission to view flags
  if (!canUserViewFlag(user)) {
    return (
      <MasterIndexPersonBase>
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="h6" color="error">
            Access Denied
          </Typography>
          <Typography variant="body2" color="textSecondary">
            You do not have permission to view flags.
          </Typography>
        </Box>
      </MasterIndexPersonBase>
    );
  }

  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedFlag, setSelectedFlag] = useState<IPersonFlag | null>(null);
  const [sidePanelMode, setSidePanelMode] = useState<'add' | 'view' | 'edit'>(
    'add',
  );
  const [menuState, setMenuState] = useState<{
    anchorEl: HTMLElement | null;
    selectedFlag: IPersonFlag | null;
  }>({ anchorEl: null, selectedFlag: null });

  const [refreshKey, setRefreshKey] = useState(0);

  const [columnSettings, setColumnSettings] = useState<DataGridColSettings[]>(
    () => {
      const savedSettings = getLocalStorageItem({
        key: String(user?._id),
        prefix: `person-flags-${LocalStoragePrefix.TableSetting}`,
        shouldParse: true,
      });

      return savedSettings || personFlagsColumnSettings;
    },
  );

  const handleRefresh = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  // Sync wanted person status with WP system flag
  // Disable auto-sync when viewing flags to prevent WP flag from disappearing
  useWantedPersonFlagSync({
    personId,
    onFlagChange: handleRefresh,
    disableAutoSync: true, // Disable auto-sync on flags page to prevent race conditions
  });

  const handleAddFlag = useCallback(() => {
    if (readOnly) return;
    setSelectedFlag(null);
    setSidePanelMode('add');
    setIsSidePanelOpen(true);
  }, [readOnly]);

  const handleViewFlag = useCallback((flag: IPersonFlag) => {
    setSelectedFlag(flag);
    setSidePanelMode('view');
    setIsSidePanelOpen(true);
  }, []);

  const handleEditFlag = useCallback(
    (flag: IPersonFlag) => {
      if (readOnly) return;
      setSelectedFlag(flag);
      setSidePanelMode('edit');
      setIsSidePanelOpen(true);
    },
    [readOnly],
  );

  const handleDeleteFlag = useCallback(
    (flag: IPersonFlag) => {
      if (readOnly) return;
      setSelectedFlag(flag);
      setIsDeleteModalOpen(true);
    },
    [readOnly],
  );

  const handleFlagSaved = useCallback(() => {
    setIsSidePanelOpen(false);
    setSelectedFlag(null);
    handleRefresh();
    // Also refresh the person data in the parent component
    if (onFlagsUpdate) {
      onFlagsUpdate();
    }
  }, [handleRefresh, onFlagsUpdate]);

  const handleFlagDeleted = useCallback(() => {
    setIsDeleteModalOpen(false);
    setSelectedFlag(null);
    handleRefresh();
    // Also refresh the person data in the parent component
    if (onFlagsUpdate) {
      onFlagsUpdate();
    }
  }, [handleRefresh, onFlagsUpdate]);

  const handleMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, flag: IPersonFlag) => {
      if (readOnly) return;
      event.stopPropagation();
      setMenuState({ anchorEl: event.currentTarget, selectedFlag: flag });
    },
    [readOnly],
  );

  const handleMenuClose = useCallback(() => {
    setMenuState({ anchorEl: null, selectedFlag: null });
  }, []);

  const handleMenuView = useCallback(() => {
    if (menuState.selectedFlag) {
      handleViewFlag(menuState.selectedFlag);
    }
    handleMenuClose();
  }, [menuState.selectedFlag, handleViewFlag, handleMenuClose]);

  const handleMenuEdit = useCallback(() => {
    if (readOnly) {
      handleMenuClose();
      return;
    }
    if (menuState.selectedFlag) {
      handleEditFlag(menuState.selectedFlag);
    }
    handleMenuClose();
  }, [menuState.selectedFlag, handleEditFlag, handleMenuClose, readOnly]);

  const handleMenuDelete = useCallback(() => {
    if (readOnly) {
      handleMenuClose();
      return;
    }
    if (menuState.selectedFlag) {
      handleDeleteFlag(menuState.selectedFlag);
    }
    handleMenuClose();
  }, [menuState.selectedFlag, handleDeleteFlag, handleMenuClose, readOnly]);

  const getAddedByText = useCallback((flag: IPersonFlag) => {
    if (flag.origin === FlagOrigin.SYSTEM) {
      const systemTypeMap = {
        [SystemFlagType.WANTED_PERSON]: 'System: Wanted Person',
        [SystemFlagType.JMS_RISK_ASSESSMENT]: 'System: JMS Risk Assessment',
        [SystemFlagType.JMS_MEDICAL_ASSESSMENT]:
          'System: JMS Medical Assessment',
      };
      return flag.systemType ? systemTypeMap[flag.systemType] : 'System';
    }
    return flag.addedBy ? formatPersonName(flag.addedBy) : 'Manual';
  }, []);

  const canEditFlag = useCallback(
    (flag: IPersonFlag) => {
      return canUserEditFlag(user, flag);
    },
    [user],
  );

  const canDeleteFlag = useCallback(() => {
    return canUserDeleteFlag(user);
  }, [user]);

  const currentPersonIdRef = useRef(personId);

  if (currentPersonIdRef.current !== personId) {
    currentPersonIdRef.current = personId;
  }

  const fetchFlagsData = useCallback(
    async (
      page: number,
      limit: number,
      filter: string,
      sort?: string,
    ): Promise<{
      rows: any[];
      totalCount?: number;
      hasNextPage?: boolean;
    }> => {
      const currentPersonId = currentPersonIdRef.current;

      if (!currentPersonId)
        return { rows: [], totalCount: 0, hasNextPage: false };

      try {
        const response = await FlagsApi.getPersonFlags(currentPersonId, {
          page,
          limit,
          filter,
          sort,
        });
        const allData = response.data || [];

        return {
          rows: allData,
          totalCount: allData.length,
          hasNextPage: false,
        };
      } catch (error: any) {
        return { rows: [], totalCount: 0, hasNextPage: false };
      }
    },
    [refreshKey],
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'flagName',
        headerName: 'Flag Name',
        width: 200,
        renderCell: (params) => {
          const flag = params.row as IPersonFlag;
          return <Typography variant="body2">{flag.flag.name}</Typography>;
        },
      },
      {
        field: 'code',
        headerName: 'Code',
        width: 100,
        renderCell: (params) => {
          const f = params.row as IPersonFlag;
          return <FlagChips flags={[f.flag]} size="small" />;
        },
      },
      {
        field: 'addedBy',
        headerName: 'Added By',
        width: 200,
        renderCell: (params) => {
          const flag = params.row as IPersonFlag;
          return (
            <Typography variant="body2">{getAddedByText(flag)}</Typography>
          );
        },
      },
      {
        field: 'dateAssigned',
        headerName: 'Date',
        width: 150,
        renderCell: (params) => {
          const flag = params.row as IPersonFlag;
          return (
            <Typography variant="body2">
              {moment(flag.dateAssigned).format('MM/DD/YYYY HH:mm')}
            </Typography>
          );
        },
      },
      {
        field: 'reason',
        headerName: 'Reason',
        width: 200,
        flex: 1,
        renderCell: (params) => {
          const flag = params.row as IPersonFlag;
          return <Typography variant="body2">{flag.reason || ''}</Typography>;
        },
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 80,
        sortable: false,
        renderCell: (params) => {
          if (readOnly) return null;
          const flag = params.row as IPersonFlag;
          return (
            <IconButton
              size="small"
              onClick={(e) => handleMenuOpen(e, flag)}
              title="Actions"
            >
              <IcDotSpread />
            </IconButton>
          );
        },
      },
    ],
    [getAddedByText, handleMenuOpen, readOnly],
  );

  const stableOnRowClick = useCallback(
    (params: any) => {
      handleViewFlag(params.row as IPersonFlag);
    },
    [handleViewFlag],
  );

  const stableSetColumnSettings = useCallback(
    (newSettings: DataGridColSettings[]) => {
      setColumnSettings(newSettings);
      setLocalStorageItem({
        key: String(user?._id),
        prefix: `person-flags-${LocalStoragePrefix.TableSetting}`,
        value: newSettings,
      });
    },
    [user?._id],
  );

  return (
    <MasterIndexPersonBase>
      <Box>
        <DataGrid
          key={refreshKey}
          name="person-flags"
          fetchData={fetchFlagsData}
          columns={columns}
          columnSettings={columnSettings}
          setColumnSettings={stableSetColumnSettings}
          onRowClick={stableOnRowClick}
          cta={
            !readOnly &&
            canUserAssignFlag(user) && (
              <Button
                variant="contained"
                color="error"
                onClick={handleAddFlag}
                disabled={!personId}
              >
                Add Flag
              </Button>
            )
          }
        />

        <FlagSidePanel
          open={isSidePanelOpen}
          onClose={() => setIsSidePanelOpen(false)}
          onSave={handleFlagSaved}
          personId={personId || ''}
          flag={selectedFlag}
          mode={sidePanelMode}
        />

        <PersonFlagDeleteModal
          open={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={handleFlagDeleted}
          flag={selectedFlag}
          personId={personId || ''}
        />

        <Menu
          anchorEl={menuState.anchorEl}
          open={Boolean(menuState.anchorEl)}
          onClose={handleMenuClose}
          MenuListProps={{
            'aria-labelledby': 'flag-actions-menu',
          }}
        >
          <MenuItem onClick={handleMenuView}>View</MenuItem>
          {!readOnly &&
            menuState.selectedFlag &&
            menuState.selectedFlag.origin === FlagOrigin.MANUAL &&
            canEditFlag(menuState.selectedFlag) && (
              <MenuItem onClick={handleMenuEdit}>Edit</MenuItem>
            )}
          {!readOnly &&
            menuState.selectedFlag &&
            menuState.selectedFlag.origin === FlagOrigin.MANUAL &&
            canDeleteFlag() && (
              <MenuItem onClick={handleMenuDelete} sx={{ color: 'error.main' }}>
                Delete
              </MenuItem>
            )}
        </Menu>
      </Box>
    </MasterIndexPersonBase>
  );
};

export default MasterIndexPersonFlags;
